import { useEffect, useState } from "react";
import { Form, Button, Modal, Container, Row, Col } from "react-bootstrap";

const SensorForm = ({ selectedSensor, onSave, showModal, handleCloseModal }) => {
    const [formData, setFormData] = useState({
        serial_number: "",
        build_revision: "",
        part_revision: "",
        oil_type: "",
        filter_thickness: 0,
        cavity: "",
        sensor_description: "",
    });

    const [formErrors, setFormErrors] = useState({
        serial_number: "",
        build_revision: "",
        part_revision: "",
        oil_type: "",
        filter_thickness: "",
        cavity: "",
        sensor_description: "",
    });

    // For editing, fill the form with its data
    useEffect(() => {
        if (selectedSensor) {
            setFormData({
                ...selectedSensor,
            });
        } else {
            // Clear the form for a new sensor
            setFormData({
                serial_number: "",
                build_revision: "",
                part_revision: "",
                oil_type: "",
                filter_thickness: 0,
                cavity: "",
                sensor_description: "",
            });
        }
    }, [selectedSensor]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "", // Clears the error for the specific field
        }));

        // Update the form data with the new value
        setFormData((prevData) => ({
            ...prevData,
            [name]: name === "created_at" ? new Date(value).toISOString().slice(0, 16) : value,
        }));
    };

    const validateForm = () => {
        const errors = {};
        let isValid = true;

        // Check if each required field is filled
        Object.keys(formData).forEach((key) => {
            if (formData[key] === "" || formData[key] === 0) {
                errors[key] = `${key.replace(/([A-Z])/g, " $1").toLowerCase()} is required.`;
                isValid = false;
            }
        });

        setFormErrors(errors);
        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSave(formData); // Save the data
        }
    };

    return (
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{selectedSensor ? "Edit Sensor" : "Add New Sensor"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container fluid>
                    <Row className="justify-content-md-center">
                        <Col xs={12}>
                            <Form onSubmit={handleSubmit}>

                                {/* Sensor Serial Number */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group controlId="formName" className="mb-3">
                                            <Form.Label>Sensor Serial Number</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="serial_number"
                                                value={formData.serial_number}
                                                onChange={handleChange}
                                                placeholder="Enter sensor serial number"
                                                isInvalid={!!formErrors.serial_number}
                                            />
                                            {formErrors.serial_number && (
                                                <p className="text-danger">{formErrors.serial_number}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Build Revision & Part Revision */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group controlId="formBuildRevision" className="mb-3">
                                            <Form.Label>Build Revision</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="build_revision"
                                                value={formData.build_revision}
                                                onChange={handleChange}
                                                placeholder="Enter build revision"
                                                isInvalid={!!formErrors.build_revision}
                                            />
                                            {formErrors.build_revision && (
                                                <p className="text-danger">{formErrors.build_revision}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formPartRevision" className="mb-3">
                                            <Form.Label>Part Revision</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="part_revision"
                                                value={formData.part_revision ?? ""}
                                                onChange={handleChange}
                                                placeholder="Enter part revision"
                                                isInvalid={!!formErrors.part_revision}
                                            />
                                            {formErrors.part_revision && (
                                                <p className="text-danger">{formErrors.part_revision}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Oil Type */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group controlId="formTypeOfOil" className="mb-3">
                                            <Form.Label>Oil Type</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="oil_type"
                                                value={formData.oil_type}
                                                onChange={handleChange}
                                                placeholder="Enter oil type"
                                                isInvalid={!!formErrors.oil_type}
                                            />
                                            {formErrors.oil_type && (
                                                <p className="text-danger">{formErrors.oil_type}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Filter Thickness & Cavity */}
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group controlId="formFilterThickness" className="mb-3">
                                            <Form.Label>Filter Thickness</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="filter_thickness"
                                                value={formData.filter_thickness}
                                                onChange={handleChange}
                                                placeholder="Enter filter thickness"
                                                isInvalid={!!formErrors.filter_thickness}
                                            />
                                            {formErrors.filter_thickness && (
                                                <p className="text-danger">{formErrors.filter_thickness}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formCavity" className="mb-3">
                                            <Form.Label>Cavity</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cavity"
                                                value={formData.cavity}
                                                onChange={handleChange}
                                                placeholder="Enter cavity"
                                                isInvalid={!!formErrors.cavity}
                                            />
                                            {formErrors.cavity && (
                                                <p className="text-danger">{formErrors.cavity}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Timestamp & Sensor description */}
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group controlId="sensorDescription" className="mb-4">
                                            <Form.Label>Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="sensor_description"
                                                value={formData.sensor_description}
                                                onChange={handleChange}
                                                placeholder="Add a description here"
                                                isInvalid={!!formErrors.sensor_description}
                                            />
                                            {formErrors.sensor_description && (
                                                <p className="text-danger">{formErrors.sensor_description}</p>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Button type="submit" variant="primary">
                                    Save
                                </Button>
                            </Form>
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
        </Modal>
    );
};

export default SensorForm;